import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import {
  ArrowRight,
  Bot,
  Check,
  Eye,
  EyeOff,
  Lock,
  Mail,
  MessageCircle,
  Radio,
  Send,
} from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);

  const shakeTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    };
  }, []);

  function triggerShake() {
    setShake(true);
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    shakeTimerRef.current = setTimeout(() => setShake(false), 450);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      setLoading(false);
      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 650);
    } catch (err) {
      setLoading(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      triggerShake();
    }
  };

  return (
    <div className="auth2-page" data-testid="login-page">
      <style>{`
        .auth2-page {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 16px;
          box-sizing: border-box;
          font-family: var(--font-body);
          background: linear-gradient(155deg, #1a0e00 0%, hsl(var(--primary)) 45%, hsl(var(--primary-strong)) 100%);
        }

        /* ---------- Animated background (full screen) ---------- */
        .auth2-hero {
          position: fixed;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .auth2-hero-glow {
          position: absolute;
          inset: -30%;
          background: conic-gradient(from 0deg, #f97316, #fb923c, #ea580c, #f59e0b, #f97316);
          opacity: 0.35;
          filter: blur(80px);
          animation: auth2-hue-cycle 14s linear infinite;
        }

        .auth2-hero-grid {
          position: absolute;
          inset: -20%;
          background-image: repeating-linear-gradient(
            45deg,
            rgba(255,255,255,0.05) 0px,
            rgba(255,255,255,0.05) 1px,
            transparent 1px,
            transparent 46px
          );
          animation: auth2-grid-pan 26s linear infinite;
        }

        .auth2-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(18px);
        }

        .auth2-orb-a {
          width: 300px;
          height: 300px;
          top: -80px;
          left: -70px;
          background: rgba(255,255,255,0.14);
          animation: auth2-float-a 11s ease-in-out infinite;
        }

        .auth2-orb-b {
          width: 240px;
          height: 240px;
          bottom: -70px;
          right: -50px;
          background: rgba(0,0,0,0.28);
          animation: auth2-float-b 13s ease-in-out infinite;
        }

        .auth2-orb-c {
          width: 190px;
          height: 190px;
          bottom: 18%;
          left: 6%;
          background: rgba(255,255,255,0.09);
          animation: auth2-float-c 15s ease-in-out infinite;
        }

        .auth2-icon {
          position: absolute;
          color: rgba(255,255,255,0.18);
        }

        .auth2-icon-1 { top: 12%; right: 13%; animation: auth2-icon-float 8s ease-in-out infinite; }
        .auth2-icon-2 { bottom: 15%; right: 20%; animation: auth2-icon-float 9.5s ease-in-out infinite 0.6s; }
        .auth2-icon-3 { top: 20%; left: 9%; animation: auth2-icon-float 10.5s ease-in-out infinite 1.2s; }
        .auth2-icon-4 { bottom: 28%; left: 18%; animation: auth2-icon-float 7.5s ease-in-out infinite 0.3s; }

        .auth2-center {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .auth2-logo-glow {
          position: relative;
          display: inline-block;
          margin-bottom: 22px;
          animation: auth2-badge-in 700ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .auth2-logo-glow::before {
          content: '';
          position: absolute;
          inset: -30px;
          background: radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%);
          animation: auth2-glow-pulse 3.4s ease-in-out infinite;
        }

        .auth2-logo-img {
          position: relative;
          width: 190px;
          height: 190px;
          object-fit: contain;
          border-radius: 24px;
          background: rgba(255,255,255,0.94);
          padding: 12px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.35);
          animation: auth2-badge-float 5s ease-in-out infinite;
        }

        /* ---------- Login card (below logo) ---------- */
        .auth2-form-wrap {
          width: 100%;
          position: relative;
        }

        .auth2-card {
          padding: 32px;
          border-radius: 22px;
          background: #141414;
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 24px 60px rgba(0,0,0,0.45);
          position: relative;
          animation: auth2-step-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .auth2-card.auth2-shake {
          animation: auth2-shake 420ms ease;
        }

        .auth2-field {
          animation: auth2-rise-in 460ms ease both;
        }

        .auth2-heading {
          margin: 0 0 6px;
          color: #FFFFFF;
          font-family: var(--font-heading);
          font-weight: 800;
          font-size: 26px;
          letter-spacing: 0.2px;
          animation: auth2-rise-in 460ms ease both;
        }

        .auth2-subheading {
          margin: 0 0 26px;
          color: #9CA3AF;
          font-size: 13px;
          animation: auth2-rise-in 460ms ease 60ms both;
        }

        .auth2-label {
          display: block;
          margin-bottom: 17px;
          color: #9CA3AF;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .auth2-input-wrap { position: relative; margin-top: 7px; }

        .auth2-input-icon {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: hsl(var(--primary));
          pointer-events: none;
        }

        .auth2-input {
          box-sizing: border-box;
          width: 100%;
          padding: 12px 13px 12px 42px;
          border: 1.5px solid rgba(255,255,255,0.10);
          border-radius: 10px;
          background: rgba(255,255,255,0.04);
          color: #F3F4F6;
          font-family: var(--font-body);
          font-size: 14px;
          outline: none;
          transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
        }

        .auth2-input::placeholder { color: #6B7280; }

        .auth2-input:focus {
          border-color: hsl(var(--primary)) !important;
          background: hsl(var(--primary) / 0.06) !important;
          box-shadow: 0 0 0 4px hsl(var(--primary) / 0.14);
        }

        .auth2-toggle-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          display: grid;
          place-items: center;
          width: 30px;
          height: 30px;
          border: none;
          border-radius: 7px;
          background: transparent;
          color: hsl(var(--primary));
          cursor: pointer;
        }

        .auth2-submit {
          position: relative;
          overflow: hidden;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 13px;
          border: none;
          border-radius: 10px;
          color: hsl(var(--primary-foreground));
          font-family: var(--font-body);
          font-size: 14px;
          font-weight: 800;
          background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-strong)));
          background-size: 200% 200%;
          box-shadow: 0 9px 22px hsl(var(--primary) / 0.28);
          animation: auth2-gradient-shift 6s ease infinite;
        }

        .auth2-submit:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 28px hsl(var(--primary) / 0.36);
        }

        .auth2-submit:disabled { cursor: not-allowed; opacity: 0.7; animation: none; }

        .auth2-error {
          margin-bottom: 17px;
          padding: 10px 12px;
          border: 1px solid rgba(248,113,113,0.4);
          border-radius: 9px;
          background: rgba(127,29,29,0.25);
          color: #FCA5A5;
          font-size: 13px;
          font-weight: 600;
          animation: auth2-rise-in 260ms ease both;
        }

        .auth2-success-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
          background: #141414;
          border-radius: 22px;
          animation: auth2-fade-in 200ms ease both;
          z-index: 5;
        }

        .auth2-success-check {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-strong)));
          box-shadow: 0 12px 28px hsl(var(--primary) / 0.4);
          animation: auth2-check-in 480ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }

        .auth2-success-text {
          font-size: 15px;
          font-weight: 800;
          color: #FFFFFF;
          animation: auth2-rise-in 400ms ease 120ms both;
        }

        .auth2-footer-text {
          margin: 20px 0 0;
          color: #6B7280;
          font-size: 11px;
          text-align: center;
        }

        /* ---------- Keyframes ---------- */
        @keyframes auth2-hue-cycle {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes auth2-float-a {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(38px, 28px) scale(1.08); }
        }

        @keyframes auth2-float-b {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-34px, -24px) scale(1.1); }
        }

        @keyframes auth2-float-c {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -28px) scale(1.05); }
        }

        @keyframes auth2-icon-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-16px) rotate(8deg); }
        }

        @keyframes auth2-grid-pan {
          from { background-position: 0 0; }
          to { background-position: 200px 200px; }
        }

        @keyframes auth2-badge-in {
          from { opacity: 0; transform: scale(0.75) rotate(-6deg); }
          to { opacity: 1; transform: scale(1) rotate(0deg); }
        }

        @keyframes auth2-badge-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes auth2-glow-pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.12); }
        }

        @keyframes auth2-gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes auth2-rise-in {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes auth2-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes auth2-step-in {
          from { opacity: 0; transform: translateX(26px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes auth2-shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-8px); }
          40%, 60% { transform: translateX(8px); }
        }

        @keyframes auth2-check-in {
          from { opacity: 0; transform: scale(0.4); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (max-width: 600px) {
          .auth2-logo-img { width: 130px; height: 130px; }
          .auth2-card { padding: 24px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .auth2-hero-glow, .auth2-hero-grid, .auth2-orb, .auth2-icon, .auth2-logo-glow,
          .auth2-logo-glow::before, .auth2-logo-img,
          .auth2-card, .auth2-field, .auth2-heading, .auth2-subheading,
          .auth2-error, .auth2-success-overlay, .auth2-success-check, .auth2-success-text,
          .auth2-submit {
            animation: none !important;
          }
        }
      `}</style>

      <section className="auth2-hero">
        <div className="auth2-hero-glow" />
        <div className="auth2-hero-grid" />
        <div className="auth2-orb auth2-orb-a" />
        <div className="auth2-orb auth2-orb-b" />
        <div className="auth2-orb auth2-orb-c" />

        <MessageCircle size={68} strokeWidth={1} className="auth2-icon auth2-icon-1" />
        <Send size={54} strokeWidth={1} className="auth2-icon auth2-icon-2" />
        <Bot size={58} strokeWidth={1} className="auth2-icon auth2-icon-3" />
        <Radio size={48} strokeWidth={1} className="auth2-icon auth2-icon-4" />

      </section>

      <section className="auth2-center">
        <div className="auth2-logo-glow">
          <img src="/adiyash-logo-hero.webp" alt="Adiyash Gym" className="auth2-logo-img" />
        </div>

        <div className="auth2-form-wrap">
          <form
            onSubmit={handleSubmit}
            className={`auth2-card ${shake ? "auth2-shake" : ""}`}
          >
            <div style={{ marginBottom: "26px" }}>
              <h1 className="auth2-heading">Welcome back</h1>
              <p className="auth2-subheading">Sign in to manage branches &amp; WhatsApp connections.</p>
            </div>

            <label className="auth2-field auth2-label" style={{ animationDelay: "120ms" }}>
              Email

              <div className="auth2-input-wrap">
                <Mail size={17} className="auth2-input-icon" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@adiyash.com"
                  autoComplete="username"
                  required
                  className="auth2-input"
                />
              </div>
            </label>

            <label className="auth2-field auth2-label" style={{ marginBottom: "18px", animationDelay: "180ms" }}>
              Password

              <div className="auth2-input-wrap">
                <Lock size={17} className="auth2-input-icon" />
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="auth2-input"
                  style={{ paddingRight: "45px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="auth2-toggle-btn"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            {error && (
              <p data-testid="login-error" className="auth2-error">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading || success}
              className="auth2-submit auth2-field"
              style={{ animationDelay: "240ms" }}
            >
              {loading ? "Signing in..." : "Sign In"}
              {!loading && <ArrowRight size={17} />}
            </button>

            <p className="auth2-footer-text auth2-field" style={{ animationDelay: "300ms" }}>
              Adiyash Automation · Secure branch access
            </p>

            {success && (
              <div className="auth2-success-overlay">
                <div className="auth2-success-check">
                  <Check size={30} color="white" strokeWidth={3} />
                </div>
                <div className="auth2-success-text">Welcome back!</div>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
